import type { TFunction } from 'i18next';

import { PERMISSION_GROUP_NAME } from 'src/constants/modulePermission';
import type { BusinessUnit } from 'src/types/businessUnit';
import type { Role, User } from 'src/types/user';
import type {
  FilterValue,
  FilterValueSpec,
} from 'src/components/app/context/FilterContext';
import { findCurrentSurveyPermissions } from 'src/components/app/context/UserContext/useUserPermissions';

/**
 * Types for modeling a filter tree.
 *
 * TODO: This structure could be also used to implement the dashboard statistics filters.
 */
export type FilterNode =
  | FilterValue
  | { name: string; childNodes: FilterNode[] };

export type FilterNodeWithChildren = { name: string; childNodes: FilterNode[] };

export type FilterTree = FilterNodeWithChildren[];

export type FilterTreeOption =
  | 'businessUnit'
  | 'role'
  | 'isUserActiveInSurvey'
  | 'permission';

export const filterSpecs: FilterValueSpec[] = [
  {
    name: new RegExp('admin:Detail.BusinessUnit'),
    required: false,
    multiple: true,
  },
  {
    name: new RegExp('admin:Detail.role'),
    required: false,
    multiple: true,
  },
  {
    name: new RegExp('admin:Detail.surveyActivationStatus'),
    required: false,
    multiple: false,
  },
  {
    name: new RegExp('admin:Permission.AccessToTool'),
    required: false,
    multiple: false,
  },
  {
    name: new RegExp('admin:Permission.UserRole'),
    required: false,
    multiple: false,
  },
  {
    name: /.*/,
    required: false,
    multiple: true,
  },
];

/**
 * Helper to construct a node id (needed for MUI tree view) from a node path
 */
export const constructNodeId = (nodePath: FilterNode[]): string => {
  const nodeIdentifiers = nodePath.map((node) =>
    'childNodes' in node ? node.name : node.label,
  );
  return nodeIdentifiers.join(', ');
};

/**
 * Assuming the given nodeId was build using {@link constructNodeId}, deconstruct the
 * path of nodeIds that "lead" to that node.
 *
 * @example
 * const result = deconstructNodeId('a, b, c'); // ['a', 'a, b', 'a, b, c']
 */
const deconstructNodeId = (nodeId: string): string[] =>
  nodeId
    .split(', ')
    .map((value, index, array) => array.slice(0, index + 1).join(', '));

/**
 * Helper function, to determine all expanded nodes in the filter tree.
 */
export const expandedNodes = (nodeIds: string[]): string[] =>
  nodeIds.flatMap(deconstructNodeId);

/**
 * Helper function to format labels for the filter display.
 */
export const formatLabel =
  (t: TFunction) => (name: string, values: FilterValue[]) =>
    `${name
      .split(',')
      .map((part) => t(part.trim()))
      .slice(-2)
      .join(', ')}: ${values
      .map((filterValue) => t(filterValue.label))
      .join(', ')}`;

/**
 * Build the actual filter tree, containing business units and user-specific module permissions.
 */
export const buildFilterTree = (
  user: User,
  businessUnits: BusinessUnit[],
  roles: Role[],
  filterTreeOptions: FilterTreeOption[] = [
    'businessUnit',
    'role',
    'isUserActiveInSurvey',
    'permission',
  ],
): FilterTree => {
  const filterTree = [];

  if (filterTreeOptions.includes('businessUnit')) {
    filterTree.push({
      name: 'admin:Detail.BusinessUnit',
      childNodes: businessUnits.map((businessUnit) => ({
        label: businessUnit.name,
        filterExpr: { business_unit: String(businessUnit.id) },
      })),
    });
  }

  if (filterTreeOptions.includes('role')) {
    filterTree.push({
      name: 'admin:Detail.role',
      childNodes: roles.map((role) => ({
        label: role.displayName,
        filterExpr: { role: String(role.id) },
      })),
    });
  }

  if (filterTreeOptions.includes('isUserActiveInSurvey')) {
    filterTree.push({
      name: 'admin:Detail.surveyActivationStatus',
      childNodes: [
        {
          label: 'admin:Detail:active',
          filterExpr: {
            survey: String(user.session.survey?.id),
            is_user_active_in_survey: '1',
          },
        },
        {
          label: 'admin:Detail.inactive',
          filterExpr: {
            survey: String(user.session.survey?.id),
            is_user_active_in_survey: '0',
          },
        },
      ],
    });
  }

  if (filterTreeOptions.includes('permission')) {
    filterTree.push({
      name: 'admin:Permission.Module',
      childNodes:
        findCurrentSurveyPermissions(user).map((permission) => ({
          name: permission.module.name,
          childNodes: [
            {
              name: 'admin:Permission.AccessToTool',
              childNodes: [
                {
                  label: 'common:yes',
                  filterExpr: {
                    module_id: String(permission.module.id),
                    has_access: '1',
                  },
                },
                {
                  label: 'common:no',
                  filterExpr: {
                    module_id: String(permission.module.id),
                    has_access: '0',
                  },
                },
              ],
            },
            {
              name: 'admin:Permission.UserRole',
              childNodes: Object.entries(PERMISSION_GROUP_NAME).map(
                ([id, label]) => ({
                  label,
                  filterExpr: {
                    module_id: String(permission.module.id),
                    permission_group: String(id),
                  },
                }),
              ),
            },
          ],
        })) ?? [],
    });
  }
  return filterTree.filter((node) => !!node);
};
